// ============================================================================
// LES REGLES PURES DE LA PORTE DE L'ECOSYSTEME
// ----------------------------------------------------------------------------
// Aucun rendu ici, aucun import de React : trois decisions que l'ecran de
// connexion prend, isolees pour etre lisibles et verifiables d'un coup d'oeil.
//
//   1. Qui porte quelle couleur dans le prisme (le logo Vyndra repeint).
//   2. Ou mene la porte d'un univers.
//   3. Quelle redirection accepter apres la connexion.
//
// AUCUNE COULEUR NI AUCUNE ADRESSE N'EST ECRITE ICI. Les couleurs viennent de
// l'identite visuelle de chaque univers (GET /hubs/public/liste), les adresses
// de `adresseEcranEspace`, la seule fonction qui a le droit d'en fabriquer.
// Un univers cree demain dans les reglages prend sa place sans qu'une ligne
// change.
// ============================================================================

import { adresseEcranEspace, adresseEspace, estAdresseDUnAutreHote } from '@/components/espace/espace-adresse';
import { lireHexa } from '@/components/espace/couleurs';
import { domaineDePartage } from '@/lib/hote-espace';

/**
 * Nombre de losanges des deux ailes du logo Vyndra (trois a gauche, trois a
 * droite). Le septieme, la clef de voute en bas du V, reste a Vyndra lui-meme.
 */
export const LOSANGES_AILES = 6;

/**
 * Ordre dans lequel on LIT les ailes pour y repartir les univers : on descend
 * l'aile gauche, on passe sous la clef de voute, on remonte l'aile droite.
 * Les nombres sont les rangs des losanges dans le dessin du logo
 * (components/vyndra-logo.tsx : 0 a 2 aile gauche de haut en bas, 3 a 5 aile
 * droite de haut en bas, 6 la clef de voute).
 *
 * Lu ainsi, le V est un chemin continu : deux univers se partagent une aile
 * chacun, trois se partagent le haut gauche, le creux et le haut droit, et la
 * figure reste symetrique quel que soit leur nombre.
 */
const PARCOURS_DU_V = [0, 1, 2, 5, 4, 3] as const;

/**
 * Combien de losanges pour chaque univers, selon leur nombre. Repartitions
 * choisies SYMETRIQUES (le V ne penche jamais d'un cote) et CONTIGUES (un
 * univers occupe un seul morceau du chemin, jamais des losanges eparpilles).
 */
const REPARTITION: Record<number, readonly number[]> = {
  1: [6],
  2: [3, 3],
  3: [2, 2, 2],
  4: [2, 1, 1, 2],
  5: [1, 1, 2, 1, 1],
  6: [1, 1, 1, 1, 1, 1],
};

export interface UniversColore {
  slug: string;
  couleur: string | null;
}

export interface PrismeColore {
  /** Couleur de chacun des six losanges des ailes, dans l'ordre du dessin. */
  losanges: string[];
  /** Pour chaque univers peint, les rangs de ses losanges dans le dessin. */
  groupes: Record<string, number[]>;
}

/**
 * Repeint le prisme aux couleurs des univers.
 *
 * Rend null quand aucun univers n'a de couleur exploitable : le logo garde alors
 * ses couleurs d'origine, plutot qu'une couleur inventee. Les univers sans
 * couleur ne recoivent pas de losange (ils restent presents dans la liste des
 * portes). Au-dela de six univers colores, seuls les six premiers peuvent
 * tenir dans les ailes : c'est une limite du dessin, pas un choix, et les
 * suivants restent tous presents dans la liste des portes.
 */
export function colorerPrisme(univers: UniversColore[]): PrismeColore | null {
  const colores = univers
    .map((u) => ({ slug: u.slug, couleur: lireHexa(u.couleur) }))
    .filter((u): u is { slug: string; couleur: string } => u.couleur !== null)
    .slice(0, LOSANGES_AILES);
  if (colores.length === 0) return null;

  const tailles = REPARTITION[colores.length];
  const losanges: string[] = new Array(LOSANGES_AILES).fill('');
  const groupes: Record<string, number[]> = {};
  let pas = 0;
  colores.forEach((u, i) => {
    groupes[u.slug] = [];
    for (let k = 0; k < tailles[i]; k += 1) {
      const rang = PARCOURS_DU_V[pas];
      losanges[rang] = u.couleur;
      groupes[u.slug].push(rang);
      pas += 1;
    }
  });
  return { losanges, groupes };
}

/**
 * L'adresse de la porte d'un univers : SON ecran de connexion, sur SON adresse.
 *
 * Passe par `adresseEcranEspace` (components/espace/espace-adresse.ts), jamais
 * par une concatenation. C'est ce qui corrige le lien casse de l'ancien ecran :
 * il ecrivait `https://${hote}`, or `hote` n'est que l'ETIQUETTE du
 * sous-domaine (« guardinformatique »), le lien menait donc a une adresse qui
 * n'existe pas.
 *
 * `hoteCourant` est l'hote de la requete, fourni par le composant serveur : le
 * rendu serveur et le rendu du navigateur calculent ainsi la meme adresse.
 */
export function adressePorte(
  univers: { slug: string; hote: string },
  hoteCourant: string | null,
): { href: string; affichee: string; autreHote: boolean } {
  const cible = { code: `hub-${univers.slug}`, sousDomaine: univers.hote, href: `/hub/${univers.slug}` };
  const ecran = adresseEcranEspace(cible, '/login', hoteCourant);
  if (!estAdresseDUnAutreHote(ecran)) {
    // Aucun domaine parent (developpement sur « localhost ») : pas d'adresse
    // propre possible. `adresseEcranEspace` rendrait alors « /login », c'est-a-dire
    // CETTE page : la porte ne menerait nulle part. On retombe sur le chemin de
    // l'univers, que le middleware protege et qui revient ici avec sa
    // redirection. Jamais un lien mort.
    const chemin = adresseEspace(cible, hoteCourant);
    return { href: chemin, affichee: chemin, autreHote: false };
  }
  let affichee = ecran;
  try {
    affichee = new URL(ecran).host;
  } catch {
    affichee = ecran;
  }
  return { href: ecran, affichee, autreHote: true };
}

/** Le parametre pose sur la porte : il dit a l'ecran d'arrivee d'ouvrir ses battants. */
export const PARAM_PORTE = 'porte';

/**
 * Ecrit le point du geste dans l'adresse de la porte, en fractions de l'ecran
 * (« 0.62,0.81 »). Rien de sensible, rien d'identifiant : un point, pour que
 * l'ecran d'arrivee ouvre ses battants la ou le doigt s'est pose.
 */
export function avecGestePorte(href: string, x: number, y: number, largeur: number, hauteur: number): string {
  const fx = Math.min(1, Math.max(0, x / Math.max(1, largeur)));
  const fy = Math.min(1, Math.max(0, y / Math.max(1, hauteur)));
  try {
    const url = new URL(href, 'http://relatif.invalid');
    url.searchParams.set(PARAM_PORTE, `${fx.toFixed(3)},${fy.toFixed(3)}`);
    return estAdresseDUnAutreHote(href) ? url.toString() : `${url.pathname}${url.search}`;
  } catch {
    return href;
  }
}

/** Relit le point du geste, ou null si le parametre est absent ou malforme. */
export function lireGestePorte(valeur: string | null): { fx: number; fy: number } | null {
  if (!valeur) return null;
  const m = /^(0(?:\.\d{1,4})?|1(?:\.0{1,4})?),(0(?:\.\d{1,4})?|1(?:\.0{1,4})?)$/.exec(valeur.trim());
  if (!m) return null;
  return { fx: Number(m[1]), fy: Number(m[2]) };
}

/**
 * La redirection demandee par `?redirect=`, si elle est SURE.
 *
 * LE DEFAUT CORRIGE. L'ancien ecran passait la valeur telle quelle a
 * `router.push`. Or le routeur suit une adresse absolue : un lien
 * `/login?redirect=https://site-pirate.example` envoyait la personne, juste
 * apres une connexion REUSSIE sur un vrai ecran Vyndra, vers une page qui
 * pouvait imiter la suite. C'est la redirection ouverte classique.
 *
 * On n'accepte donc qu'un CHEMIN de cette application : il commence par une
 * seule barre oblique. « //site.example » et « /\site.example » sont des
 * adresses d'un autre site deguisees en chemin, refusees. Tout le reste est
 * ignore, et la destination normale (decidee par l'hote) s'applique.
 *
 * Le middleware, lui, ne pose jamais qu'un chemin (`pathname`) : ce filtre ne
 * retire rien a l'usage legitime.
 */
export function redirectionSure(valeur: string | null): string | null {
  if (!valeur) return null;
  const brut = valeur.trim();
  if (!brut.startsWith('/')) return null;
  if (brut.startsWith('//') || brut.startsWith('/\\')) return null;
  // Aucun caractere de controle ni barre inverse : un navigateur les
  // normalise parfois en barre oblique, ce qui recreerait « // ».
  if (/[\u0000-\u001F\u007F\\]/.test(brut)) return null;
  return brut;
}

/**
 * L'adresse de la proposition « renforcez votre mot de passe » (revue du 27/09/2026,
 * A4). Un Proprietaire ou un Administrateur dont le mot de passe ne suit plus les
 * regles y passe apres sa connexion, AVANT sa destination, qui voyage en « suite ».
 * La suite est ABSOLUE : sur un hote d'univers, /settings renvoie vers app., et un
 * chemin seul ramenerait sur le mauvais hote.
 */
export function adresseRenforcement(destination: string, origine: string): string {
  let suite = destination;
  try {
    suite = new URL(destination, origine).toString();
  } catch {
    /* destination deja absolue ou illisible : validee a l'arrivee par suiteSure */
  }
  return `/settings/security?renforcer=1&suite=${encodeURIComponent(suite)}`;
}

/**
 * La suite acceptee apres la proposition de renforcement. Un chemin (meme regle que
 * redirectionSure), ou une adresse ABSOLUE dont l'hote partage le domaine de l'hote
 * courant (app.vyndra.fr et guardinformatique.vyndra.fr), avec le meme protocole.
 * Tout le reste est refuse : sans ce filtre, le lien deviendrait une redirection
 * ouverte vers n'importe quel site.
 */
export function suiteSure(
  valeur: string | null,
  hoteCourant: string,
  protocoleCourant: string,
): string | null {
  const chemin = redirectionSure(valeur);
  if (chemin) return chemin;
  if (!valeur) return null;
  let url: URL;
  try {
    url = new URL(valeur.trim());
  } catch {
    return null;
  }
  if (url.protocol !== protocoleCourant) return null;
  if (url.username || url.password) return null;
  const courant = hoteCourant.trim().toLowerCase();
  if (url.host.toLowerCase() === courant) {
    return redirectionSure(url.pathname + url.search + url.hash);
  }
  const domaine = domaineDePartage(courant);
  if (domaine === null || !url.hostname.toLowerCase().endsWith(domaine)) return null;
  return url.toString();
}
